import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronDown, Edit2, Trash2 } from 'lucide-react';
import { AccountNode, BancoMCPReconciliationAccount, Department, DEPARTMENTS } from '../services/apiService';
import { SaldoMCPBadge } from './ui/SaldoMCPBadge';

interface AccountTreeProps {
  accounts: AccountNode[];
  canWrite: boolean;
  onEdit?: (fullPath: string) => void;
  onDelete?: (fullPath: string) => void;
  onUpdateDepartment?: (fullPath: string, department: Department | null) => Promise<void>;
  reconciliationMap?: Record<string, BancoMCPReconciliationAccount>;
}

function isExpenseAccount(fullPath: string): boolean {
  return fullPath === 'Despesas' || fullPath.startsWith('Despesas:');
}

function DepartmentCell({
  node,
  canWrite,
  onUpdateDepartment,
}: {
  node: AccountNode;
  canWrite: boolean;
  onUpdateDepartment?: (fullPath: string, department: Department | null) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Hide entirely for non-Despesas accounts and for the Despesas root itself
  if (!isExpenseAccount(node.full_path) || node.full_path === 'Despesas') {
    return <td className="py-1.5 pl-2 pr-2 text-sm text-gray-300" data-testid="department-cell-empty" />;
  }

  const dept = node.department ?? null;
  const inherited = !!node.department_inherited;

  const handleChange = async (value: string) => {
    if (!onUpdateDepartment) return;
    setSaving(true);
    try {
      const newDept: Department | null = value === '' ? null : (value as Department);
      await onUpdateDepartment(node.full_path, newDept);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (editing && canWrite && onUpdateDepartment) {
    return (
      <td className="py-1.5 pl-2 pr-2 text-sm whitespace-nowrap">
        <select
          autoFocus
          disabled={saving}
          value={inherited ? '' : dept ?? ''}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={() => setEditing(false)}
          className="border rounded px-1 py-0.5 text-xs"
          data-testid={`department-select-${node.full_path}`}
        >
          <option value="">Limpar (herdar)</option>
          {DEPARTMENTS.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
      </td>
    );
  }

  const display = dept ?? '—';
  const isClickable = canWrite && !!onUpdateDepartment;
  const colorClass = dept === null
    ? 'text-gray-400'
    : inherited
      ? 'text-gray-400'
      : 'text-gray-700';

  return (
    <td className="py-1.5 pl-2 pr-2 whitespace-nowrap" data-testid={`department-cell-${node.full_path}`}>
      <button
        type="button"
        disabled={!isClickable}
        onClick={() => isClickable && setEditing(true)}
        className={`text-xs ${colorClass} ${isClickable ? 'hover:underline cursor-pointer' : 'cursor-default'}`}
        title={
          inherited && node.department
            ? 'Herdado da conta pai'
            : isClickable
              ? 'Clique para alterar'
              : ''
        }
      >
        {display}
        {inherited && dept && (
          <span className="ml-1 text-gray-400">(herdado)</span>
        )}
      </button>
    </td>
  );
}

function AccountTreeNode({
  node,
  depth,
  canWrite,
  onEdit,
  onDelete,
  onUpdateDepartment,
  reconciliationMap,
}: {
  node: AccountNode;
  depth: number;
  canWrite: boolean;
  onEdit?: (fullPath: string) => void;
  onDelete?: (fullPath: string) => void;
  onUpdateDepartment?: (fullPath: string, department: Department | null) => Promise<void>;
  reconciliationMap?: Record<string, BancoMCPReconciliationAccount>;
}) {
  const [expanded, setExpanded] = useState(depth < 2);
  const navigate = useNavigate();
  const hasChildren = node.children && node.children.length > 0;
  const balanceBRL = node.balances?.['BRL'] ?? 0;

  const handleAccountClick = () => {
    navigate(`/extrato?account=${encodeURIComponent(node.full_path)}`);
  };

  return (
    <>
      <tr className="hover:bg-blue-50 group even:bg-gray-100">
        <td
          className={`py-1.5 pr-8 ${depth === 0 ? 'font-semibold text-gray-900' : 'text-gray-700'}`}
          style={{ paddingLeft: `${depth * 20 + 8}px` }}
        >
          <div className="flex items-center gap-1">
            {hasChildren ? (
              <button
                onClick={() => setExpanded(!expanded)}
                className="p-0.5 rounded hover:bg-gray-200"
              >
                {expanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                )}
              </button>
            ) : (
              <span className="w-5" />
            )}
            <button
              onClick={handleAccountClick}
              className="truncate text-sm text-left hover:text-blue-600 hover:underline"
              title={`Ver extrato de ${node.full_path}`}
            >
              {node.account}
            </button>
            <SaldoMCPBadge record={reconciliationMap?.[node.full_path]} />
          </div>
        </td>
        <td className="py-1.5 text-right whitespace-nowrap">
          {balanceBRL !== 0 && (
            <span
              className={`text-sm tabular-nums ${
                balanceBRL < 0 ? 'text-red-600' : 'text-green-700'
              }`}
            >
              {new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }).format(Math.abs(balanceBRL))}
            </span>
          )}
        </td>
        <DepartmentCell
          node={node}
          canWrite={canWrite}
          onUpdateDepartment={onUpdateDepartment}
        />
        <td className="py-1.5 pl-2 whitespace-nowrap">
          <div className="flex items-center gap-1">
            {canWrite && onEdit && (
              <button
                onClick={() => onEdit(node.full_path)}
                className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200"
                title="Editar conta"
              >
                <Edit2 className="h-3.5 w-3.5 text-gray-400" />
              </button>
            )}
            {canWrite && onDelete && (
              <button
                onClick={() => onDelete(node.full_path)}
                className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200"
                title="Excluir conta"
              >
                <Trash2 className="h-3.5 w-3.5 text-red-400" />
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && hasChildren &&
        node.children.map((child) => (
          <AccountTreeNode
            key={child.full_path}
            node={child}
            depth={depth + 1}
            canWrite={canWrite}
            onEdit={onEdit}
            onDelete={onDelete}
            onUpdateDepartment={onUpdateDepartment}
            reconciliationMap={reconciliationMap}
          />
        ))
      }
    </>
  );
}

export const AccountTree: React.FC<AccountTreeProps> = ({ accounts, canWrite, onEdit, onDelete, onUpdateDepartment, reconciliationMap }) => {
  if (!accounts || accounts.length === 0) {
    return <p className="text-gray-500 text-sm py-4">Nenhuma conta encontrada.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="mx-auto">
        <thead>
          <tr className="text-xs text-gray-500 uppercase">
            <th className="py-1.5 pr-8 text-left font-medium">Conta</th>
            <th className="py-1.5 pl-2 text-right font-medium">Saldo</th>
            <th className="py-1.5 pl-2 pr-2 text-left font-medium">Departamento</th>
            <th className="py-1.5 pl-2 font-medium" />
          </tr>
        </thead>
        <tbody>
          {accounts.map((node) => (
            <AccountTreeNode
              key={node.full_path}
              node={node}
              depth={0}
              canWrite={canWrite}
              onEdit={onEdit}
              onDelete={onDelete}
              onUpdateDepartment={onUpdateDepartment}
              reconciliationMap={reconciliationMap}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};
